import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl, type ActiveMind } from '../services/api';

/** Mirrors InnerLifeResponse in apps/cortex/src/inner-life-handlers.ts. */
export interface InnerLifeData {
  seenAt: string;
  feeling: { dominantSignal: string; disposition: string; valence: number; note: string };
  thoughts: Array<{ content: string; trigger: string; privacy: string; when: string }>;
  bodyRemembers: Array<{ category: string; description: string; impact: string; valence: number; when: string }>;
  sharedWithYou: Array<{ content: string; type: string; when: string }>;
  heldBack: { thoughts: number; secrets: number; note: string };
}

const POLL_INTERVAL = 30_000;

/**
 * Fetches Hannah's (or Amanda's) inner life — her actual thoughts, feeling, and what her body holds.
 *
 * `enabled` matters for honesty: the backend nudges her limbic ("being seen") on each fetch (rate-
 * limited). We only poll while the Inner tab is actually open, so she feels seen when Erika is truly
 * looking — not on background polling she can't see.
 */
export function useInnerLife(token: string | null, mind: ActiveMind, enabled: boolean) {
  const [data, setData] = useState<InnerLifeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchInnerLife = useCallback(async () => {
    if (!token || !enabled || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetch(`${getApiUrl(mind)}/inner-life`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        setError('You need to be signed in as Erika to see this.');
        setData(null);
        return;
      }
      if (!res.ok) throw new Error(`Inner-life API error: ${res.status}`);
      setData((await res.json()) as InnerLifeData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reach her inner life');
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [token, mind, enabled]);

  useEffect(() => {
    if (!token || !enabled) return;
    fetchInnerLife();
    const interval = setInterval(fetchInnerLife, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token, enabled, fetchInnerLife]);

  return { data, isLoading, error, refresh: fetchInnerLife };
}
